import { Action, Reducer } from 'redux';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as TeamsState from './TeamsState';
import { TokenHelper } from '../TokenHelper';
import { AppThunkAction } from '.';
import { SortHelper } from '../SortHelper';
import { Contact } from '../interfaces/Contact';
import { ContactCustomerRole } from '../interfaces/ContactCustomerRole';
import { KnownDataAction } from './Data';
import { KnownCustomersAction } from './customers';
import { KnownConsultantAction } from './consultants';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.


export interface ContactsState {
    contacts: Contact[] | undefined;
    contactsEdit: Contact | null;
    contactsLoading: boolean;
    contactQuickView: Contact | null;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

interface RequestContactsAction { type: 'REQUEST_CONTACTS' }
interface ReceiveContactsAction { type: 'RECEIVE_CONTACTS'; contacts: Contact[]; }
interface EditContactAction { type: 'EDIT_CONTACT'; contact: Contact | null; }
interface PatchContactAction { type: 'PATCH_CONTACT'; contact: Contact; }
interface RequestContactRolesAction { type: 'REQUEST_CONTACTSROLE'; contactid: string }
interface PatchContactCustomerRoleAction { type: 'PATCH_CONTACTCUSTOMERROLE'; contactid: string, contactCustomerRoles: ContactCustomerRole[]; }
interface InvalidateContactCustomerRoleAction { type: 'INVALIDATE_CONTACTROLE'; contactid: string }
interface ReloadContactsAction { type: 'RELOAD_CONTACT' }
interface QuickViewContactAction { type: 'QV_CONTACT'; contact: Contact | null; }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownContactsAction = RequestContactsAction
    | ReceiveContactsAction
    | EditContactAction
    | PatchContactAction
    | RequestContactRolesAction
    | PatchContactCustomerRoleAction
    | InvalidateContactCustomerRoleAction
    | ReloadContactsAction
    | QuickViewContactAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const ContactsActionCreators = {
    requestContacts: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownContactsAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONTACTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Contacts`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Contact[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_CONTACTS', contacts: data });
                                data.forEach(c => {
                                    if (c.contactid) {
                                        dispatch({ type: 'REQUEST_CONTACTSROLE', contactid: c.contactid });
                                        const contactIdTemp: string = c.contactid;
                                        fetch(`api/Contacts/` + contactIdTemp + '/ContactCustomerRoles', {
                                            method: "get",
                                            headers: {
                                                "Content-Type": "application/json",
                                                authorization: "bearer " + token,
                                            },
                                            mode: "cors",
                                            cache: "default",
                                        })
                                            .then(response => {
                                                if (response.status === 403) {
                                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                                }
                                                return response.json() as Promise<ContactCustomerRole[]>;
                                            })
                                            .then(data => {
                                                dispatch({ type: 'PATCH_CONTACTCUSTOMERROLE', contactid: contactIdTemp, contactCustomerRoles: data });
                                            });
                                    }
                                });
                            });
                    }
                });
        }
    },
    requestContactsRoles: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, contactid: string): AppThunkAction<KnownContactsAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_CONTACTSROLE', contactid: contactid });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Contacts/` + contactid + '/ContactCustomerRoles', {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ContactCustomerRole[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'PATCH_CONTACTCUSTOMERROLE', contactid: contactid, contactCustomerRoles: data });
                            });
                    }
                });
        }
    },
    requestContactEdition: (contact: Contact | null): AppThunkAction<KnownContactsAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_CONTACT', contact: contact });
    },
    requestInvalidateContactRoles: (contactid: string): AppThunkAction<KnownContactsAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'INVALIDATE_CONTACTROLE', contactid: contactid });
    },
    patchContactEdition: (contact: Contact): AppThunkAction<KnownContactsAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_CONTACT', contact: contact });
    },
    ReloadContactsEdition: (): AppThunkAction<KnownContactsAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_CONTACT' });
    },
    requestContactQuickView: (contact: Contact | null): AppThunkAction<KnownContactsAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_CONTACT', contact: contact });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducerContacts: Reducer<ContactsState> = (state: ContactsState | undefined, incomingAction: Action): ContactsState => {
    if (state === undefined) {
        return { contacts: undefined, contactsLoading: false, contactsEdit: null, contactQuickView: null };
    }

    const action = incomingAction as KnownContactsAction;
    switch (action.type) {
        case 'REQUEST_CONTACTS':
            return { ...state, contactsLoading: true };
        case 'RECEIVE_CONTACTS':
            return { ...state, contactsLoading: false, contacts: action.contacts.sort(SortHelper.Contacts) };
        case 'RELOAD_CONTACT':
            return { ...state, contacts: undefined };
        case 'EDIT_CONTACT':
            return { ...state, contactsEdit: action.contact };
        case 'PATCH_CONTACT': {
            const ns = { ...state };
            if (ns.contacts && action.contact.contactid) {
                const c = ns.contacts.findIndex(e => e.contactid === action.contact.contactid);
                if (c !== -1) {
                    ns.contacts[c] = action.contact;
                    // If quickView element is the one we update, force update
                    if (state.contactQuickView && state.contactQuickView.contactid === action.contact.contactid) {
                        ns.contactQuickView = { ...action.contact };
                    }
                }
                else {
                    ns.contacts.push(action.contact);
                }
                ns.contacts = [...ns.contacts.sort(SortHelper.Contacts)];
                return ns;
            }
            return state;
        }
        case 'PATCH_CONTACTCUSTOMERROLE': {
            const ns = { ...state };
            if (ns.contacts) {
                const c = ns.contacts.findIndex(e => e.contactid === action.contactid);
                if (c !== -1) {
                    ns.contacts[c].contactCustomerRole = action.contactCustomerRoles;
                    ns.contacts[c].contactCustomerRoleLoading = false;
                    // If quickView element is the one we update, force update
                    if (state.contactQuickView && state.contactQuickView.contactid === action.contactid) {
                        ns.contactQuickView = { ...ns.contacts[c] };
                    }
                    ns.contacts = [...ns.contacts.sort(SortHelper.Contacts)];
                    return ns;
                }
            }
            return state;
        }
        case 'REQUEST_CONTACTSROLE': {
            const ns = { ...state };
            if (ns.contacts) {
                const c = ns.contacts.findIndex(e => e.contactid === action.contactid);
                if (c !== -1) {
                    ns.contacts[c].contactCustomerRoleLoading = true;
                    // If quickView element is the one we update, force update
                    if (state.contactQuickView && state.contactQuickView.contactid === action.contactid) {
                        ns.contactQuickView = { ...ns.contacts[c] };
                    }
                    ns.contacts = [...ns.contacts.sort(SortHelper.Contacts)];
                    return ns;
                }
            }
            return state;
        }
        case 'INVALIDATE_CONTACTROLE': {
            const ns = { ...state };
            if (ns.contacts) {
                const c = ns.contacts.findIndex(e => e.contactid === action.contactid);
                if (c !== -1) {
                    ns.contacts[c].contactCustomerRole = null;
                    ns.contacts[c].contactCustomerRoleLoading = false;
                    // If quickView element is the one we update, force update
                    if (state.contactQuickView && state.contactQuickView.contactid === action.contactid) {
                        ns.contactQuickView = { ...ns.contacts[c] };
                    }
                    ns.contacts = [...ns.contacts.sort(SortHelper.Contacts)];
                    return ns;
                }
            }
            return state;
        }
        case 'QV_CONTACT': {
            return { ...state, contactQuickView: action.contact };
        }
        default: {
            break;
        }
    }
    // For the QuickView is selected on an other, clear this one
    const actionData = incomingAction as KnownDataAction;
    switch (actionData.type) {
        case 'QV_CLEAR':
        case 'QV_INVOICE':
        case 'QV_PROJECT':
        case 'QV_PROJECTASSIGMENT':
        case 'QV_TIMESHEET':
        case 'QV_PRODUCT': {
            return { ...state, contactQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionCustomer = incomingAction as KnownCustomersAction;
    switch (actionCustomer.type) {
        case 'QV_CUSTOMER': {
            return { ...state, contactQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionConsultant = incomingAction as KnownConsultantAction;
    switch (actionConsultant.type) {
        case 'QV_CONSULTANT': {
            return { ...state, contactQuickView: null };
        }
        default: {
            break;
        }
    }
    return state;
};